import { useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { getRecognitions } from 'api/recognitions.service';
import { AllStatuses } from 'interfaces';
import { useToast } from 'hooks/ui';
import { Recognition } from 'models/recognition.model';
import { setRecognitions, useRecognitionStore } from 'store/recognition';
import { DURATIONS } from 'common/constants';
import { recognitionKeys } from './queryKeys';

export const useRecognitionById = (id: string) => {
  const queryClient = useQueryClient();
  const all = queryClient.getQueryData<Recognition[]>(recognitionKeys.all);
  const active = all?.filter((b) => b.id === id);

  return active ? active[0] : null;
};

export const useRecognitionGetAllQuery = () => {
  const { draft } = useRecognitionStore();
  const toast = useToast();

  const sortRecognitions = useCallback(
    (data: Recognition[]) =>
      data.sort((a, b) => {
        if (a.completedTasks > b.completedTasks) {
          return 1;
        }
        if (a.completedTasks < b.completedTasks) {
          return -1;
        }
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();

        return dateA - dateB;
      }),
    [],
  );

  const isDraftSaved = draft.status !== AllStatuses.NOT_SAVED;
  const isPictureReward = draft.reward === 'PICTURE';

  const query = useQuery({
    queryKey: recognitionKeys.all,
    queryFn: () => getRecognitions({}),
    staleTime: 0,
    refetchInterval: isDraftSaved && DURATIONS.MINUTES_5,
    refetchIntervalInBackground: isDraftSaved,
    refetchOnReconnect: isDraftSaved,
    refetchOnWindowFocus: isDraftSaved && !isPictureReward,
    select: sortRecognitions,
  });

  const { data, error } = query;

  useEffect(() => {
    if (data) {
      setRecognitions(data);
    }
  }, [data]);

  useEffect(() => {
    if (error) toast.error(error.message);
  }, [error, toast]);

  return query;
};
